import { useEffect, useRef, useCallback } from 'react';
import { LineSeries } from 'lightweight-charts';
import { sma, ema, rsi, bollingerBands, INDICATOR_DEFAULTS } from '../utils/indicators';

export function useIndicators({ chartRef, chartVersion, data, activeIndicators }) {
  const indicatorSeriesRef = useRef({});

  const removeAll = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    Object.values(indicatorSeriesRef.current).forEach(s => {
      try { chart.removeSeries(s); } catch (e) {}
    });
    indicatorSeriesRef.current = {};
  }, [chartRef]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !data.length) return;

    removeAll();

    // RSI pane if needed
    if (activeIndicators.RSI) {
      const rsiData = rsi(data, INDICATOR_DEFAULTS.RSI.period);
      const series = chart.addSeries(LineSeries, {
        color: INDICATOR_DEFAULTS.RSI.color,
        lineWidth: 1.5,
        lastValueVisible: true,
        priceLineVisible: false,
        priceScaleId: 'rsi',
      });
      chart.priceScale('rsi').applyOptions({
        scaleMargins: { top: 0.7, bottom: 0 },
        autoScale: true,
      });
      series.setData(rsiData);
      indicatorSeriesRef.current.RSI = series;

      // Overbought/oversold lines
      const obLine = chart.addSeries(LineSeries, {
        color: 'rgba(246, 70, 93, 0.3)',
        lineWidth: 1,
        lastValueVisible: false,
        priceLineVisible: false,
        priceScaleId: 'rsi',
      });
      obLine.setData(rsiData.map(d => ({ time: d.time, value: 70 })));
      indicatorSeriesRef.current.RSI_OB = obLine;

      const osLine = chart.addSeries(LineSeries, {
        color: 'rgba(14, 203, 129, 0.3)',
        lineWidth: 1,
        lastValueVisible: false,
        priceLineVisible: false,
        priceScaleId: 'rsi',
      });
      osLine.setData(rsiData.map(d => ({ time: d.time, value: 30 })));
      indicatorSeriesRef.current.RSI_OS = osLine;
    }

    // Remove RSI pane if not active
    if (!activeIndicators.RSI && indicatorSeriesRef.current.RSI) {
      try { chart.removeSeries(indicatorSeriesRef.current.RSI); } catch (e) {}
      try { chart.removeSeries(indicatorSeriesRef.current.RSI_OB); } catch (e) {}
      try { chart.removeSeries(indicatorSeriesRef.current.RSI_OS); } catch (e) {}
      delete indicatorSeriesRef.current.RSI;
      delete indicatorSeriesRef.current.RSI_OB;
      delete indicatorSeriesRef.current.RSI_OS;
    }

    if (activeIndicators.SMA) {
      INDICATOR_DEFAULTS.SMA.periods.forEach((p, i) => {
        const smaData = sma(data, p);
        const series = chart.addSeries(LineSeries, {
          color: i === 0 ? '#FF9800' : i === 1 ? '#FFD54F' : '#FFB74D',
          lineWidth: 1.5,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        series.setData(smaData);
        indicatorSeriesRef.current[`SMA_${p}`] = series;
      });
    }

    if (activeIndicators.EMA) {
      INDICATOR_DEFAULTS.EMA.periods.forEach((p, i) => {
        const emaData = ema(data, p);
        const series = chart.addSeries(LineSeries, {
          color: i === 0 ? '#E040FB' : '#CE93D8',
          lineWidth: 1.5,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        series.setData(emaData);
        indicatorSeriesRef.current[`EMA_${p}`] = series;
      });
    }

    if (activeIndicators.BollingerBands) {
      const bbData = bollingerBands(data, INDICATOR_DEFAULTS.BollingerBands.period, INDICATOR_DEFAULTS.BollingerBands.stdDev);
      const upper = chart.addSeries(LineSeries, {
        color: 'rgba(79, 195, 247, 0.5)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false,
      });
      upper.setData(bbData.map(d => ({ time: d.time, value: d.upper })));
      indicatorSeriesRef.current.BB_upper = upper;

      const middle = chart.addSeries(LineSeries, {
        color: '#4FC3F7', lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false,
      });
      middle.setData(bbData.map(d => ({ time: d.time, value: d.middle })));
      indicatorSeriesRef.current.BB_middle = middle;

      const lower = chart.addSeries(LineSeries, {
        color: 'rgba(79, 195, 247, 0.5)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false,
      });
      lower.setData(bbData.map(d => ({ time: d.time, value: d.lower })));
      indicatorSeriesRef.current.BB_lower = lower;
    }

    return () => {
      removeAll();
    };
  }, [chartRef, chartVersion, data, activeIndicators, removeAll]);
}
